import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Container,
    Slide,
    useTheme,
    useMediaQuery,
    alpha
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { hasUserDecided, setConsent, DEFAULT_PREFERENCES, getConsent } from '../../utils/cookieConsent';
import CookieSettingsModal from './CookieSettingsModal';

const CookieBanner = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [show, setShow] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Don't show banner on dashboard/app routes
    const isExcludedRoute = location.pathname.startsWith('/dashboard') ||
        location.pathname.startsWith('/admin') ||
        location.pathname.startsWith('/app');

    useEffect(() => {
        // Check if user has already made a choice AND it's not an excluded route
        if (!hasUserDecided() && !isExcludedRoute) {
            const timer = setTimeout(() => setShow(true), 1500);
            return () => clearTimeout(timer);
        } else if (isExcludedRoute) {
            setShow(false);
        }
    }, [location.pathname, isExcludedRoute]);

    const handleAcceptAll = () => {
        const allAccepted = { essential: true, analytics: true, marketing: true };
        setConsent(allAccepted);
        setShow(false);
    };

    const handleDeclineAll = () => {
        const onlyEssential = { essential: true, analytics: false, marketing: false };
        setConsent(onlyEssential);
        setShow(false);
    };

    const handleSaveSettings = (prefs) => {
        setConsent(prefs);
        setShow(false);
    };

    const handleOpenSettings = () => {
        setSettingsOpen(true);
    };

    // Re-open if triggered from footer (via custom event)
    useEffect(() => {
        const handleReopen = () => setSettingsOpen(true);
        window.addEventListener('reopenCookieSettings', handleReopen);
        return () => window.removeEventListener('reopenCookieSettings', handleReopen);
    }, []);

    return (
        <>
            <Slide direction="up" in={show} mountOnEnter unmountOnExit>
                <Paper
                    elevation={12}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        left: { xs: 10, sm: 20 },
                        right: { xs: 10, sm: 20 },
                        zIndex: 9999,
                        borderRadius: 3,
                        p: { xs: 2.5, sm: 3 },
                        borderColor: theme.palette.divider,
                        background: theme.palette.background.paper,
                        backdropFilter: 'blur(10px)',
                        maxWidth: { lg: '1200px' },
                        mx: 'auto'
                    }}
                >
                    <Container maxWidth="lg" disableGutters>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { xs: 'flex-start', md: 'center' },
                                gap: 3
                            }}
                        >
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5, fontSize: '1.125rem' }}>
                                    🍪 {t('cookieConsent.bannerTitle', 'Cookie Consent')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                    {t('cookieConsent.bannerMessage', 'We use cookies to improve your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.')}{' '}
                                    <Link to="/cookie-policy" style={{ color: theme.palette.primary.contrastText, fontWeight: 600 }}>
                                        {t('cookieConsent.privacyPolicy', 'Read Cookie Policy')}
                                    </Link>
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    width: { xs: '100%', md: 'auto' },
                                    flexWrap: 'wrap'
                                }}
                            >
                                <Button
                                    onClick={handleOpenSettings}
                                    variant="outlined"
                                    size={isMobile ? 'small' : 'medium'}
                                    sx={{
                                        flexGrow: { xs: 1, md: 0 },
                                        fontWeight: 600,
                                        borderColor: alpha(theme.palette.text.primary, 0.1),
                                        color: 'primary.contrastText',
                                        '&:hover': {
                                            borderColor: alpha(theme.palette.text.primary, 0.2),
                                            bgcolor: alpha(theme.palette.text.primary, 0.04)
                                        }
                                    }}
                                >
                                    {t('cookieConsent.settings', 'Settings')}
                                </Button>
                                <Button
                                    onClick={handleDeclineAll}
                                    variant="outlined"
                                    size={isMobile ? 'small' : 'medium'}
                                    sx={{
                                        flexGrow: { xs: 1, md: 0 },
                                        fontWeight: 600,
                                        borderColor: alpha(theme.palette.text.primary, 0.1),
                                        color: 'primary.contrastText',
                                        '&:hover': {
                                            borderColor: alpha(theme.palette.text.primary, 0.2),
                                            bgcolor: alpha(theme.palette.text.primary, 0.04)
                                        }
                                    }}
                                >
                                    {t('cookieConsent.declineAll', 'Decline All')}
                                </Button>
                                <Button
                                    onClick={handleAcceptAll}
                                    variant="contained"
                                    size={isMobile ? 'small' : 'medium'}
                                    sx={{
                                        flexGrow: { xs: 1, md: 0 },
                                        fontWeight: 700,
                                        backgroundColor: theme.palette.primary.main,
                                        color: theme.palette.primary.contrastText,
                                        px: 3,
                                        '&:hover': {
                                            backgroundColor: '#8FD760',
                                        }
                                    }}
                                >
                                    {t('cookieConsent.acceptAll', 'Accept All')}
                                </Button>
                            </Box>
                        </Box>
                    </Container>
                </Paper>
            </Slide>

            <CookieSettingsModal
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                preferences={getConsent() || DEFAULT_PREFERENCES}
                onSave={handleSaveSettings}
            />
        </>
    );
};

export default CookieBanner;
